import React from 'react';
import { Icon } from '../icons.jsx';
import { supabase } from '../integrations/supabase/client';
import { useAuth } from '../auth/AuthContext.jsx';
import { confirmDialog } from '../components.jsx';

const { useState, useEffect, useCallback } = React;

const ROLE_OPTIONS = [
  ['admin', 'Admin', 'sem-violet'],
  ['redactor', 'Redactor', 'sem-info'],
  ['revisor', 'Revisor', 'sem-warning'],
  ['firmante', 'Firmante', 'sem-success'],
  ['viewer', 'Viewer', 'sem-neutral'],
];

function roleClass(role) {
  return (ROLE_OPTIONS.find((r) => r[0] === role) || [, , 'sem-neutral'])[2];
}

function toast(text, icon = 'Check') {
  window.dispatchEvent(new CustomEvent('lizzi:toast', { detail: { text, icon } }));
}

// =====================================================
// Gestión de usuarios (solo admins) — alta vía edge function
// =====================================================
export function UserManagement() {
  const { isAdmin, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const { data, error: err } = await supabase.functions.invoke('admin-list-users');
    setLoading(false);
    if (err) { setError('No se pudo cargar la lista de usuarios.'); return; }
    if (data?.error) { setError(typeof data.error === 'string' ? data.error : 'Acceso denegado.'); return; }
    setUsers(data?.users || []);
  }, []);

  useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [isAdmin, load]);

  const changeRole = async (u, newRole) => {
    // Reemplaza los roles del usuario por el seleccionado (modelo de un rol principal)
    const { error: delErr } = await supabase.from('user_roles').delete().eq('user_id', u.id);
    if (delErr) { toast('No se pudo actualizar el rol.', 'AlertTriangle'); return; }
    const { error: insErr } = await supabase.from('user_roles').insert({ user_id: u.id, role: newRole });
    if (insErr) { toast('No se pudo actualizar el rol.', 'AlertTriangle'); return; }
    toast(`Rol actualizado a ${newRole}.`);
    load();
  };

  const deleteUser = async (u) => {
    if (!(await confirmDialog({ title: 'Eliminar usuario', message: `¿Eliminar la cuenta de ${u.email}? Esta acción no se puede deshacer.`, confirmLabel: 'Eliminar', tone: 'danger', icon: 'Trash' }))) return;
    const { data, error: err } = await supabase.functions.invoke('admin-delete-user', { body: { user_id: u.id } });
    if (err || data?.error) { toast(data?.error || 'No se pudo eliminar.', 'AlertTriangle'); return; }
    toast('Usuario eliminado.');
    load();
  };

  if (!isAdmin) {
    return (
      <div style={{padding:'14px 16px', background:'var(--bg-2)', borderRadius:8, fontSize:13, color:'var(--text-3)', display:'flex', gap:10, alignItems:'center'}}>
        <Icon.Shield size={14}/> La gestión de cuentas está reservada a administradores.
      </div>
    );
  }

  return (
    <>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14}}>
        <span style={{fontSize:12.5, color:'var(--text-3)'}}>
          {loading ? 'Cargando…' : `${users.length} usuario${users.length === 1 ? '' : 's'}`}
        </span>
        <button className="btn btn-primary" style={{fontSize:13}} onClick={()=>setShowCreate(true)}>
          <Icon.Plus size={14}/> Crear usuario
        </button>
      </div>

      {error && <div style={{fontSize:12.5, color:'var(--sem-danger)', marginBottom:12}}>{error}</div>}

      <div className="card" style={{overflow:'hidden', border:'1px solid var(--line-1)'}}>
        <table className="tbl">
          <thead><tr><th>Usuario</th><th>Correo</th><th style={{width:150}}>Rol</th><th style={{width:60}}></th></tr></thead>
          <tbody>
            {users.map((u) => {
              const primaryRole = u.roles[0] || 'viewer';
              return (
                <tr key={u.id}>
                  <td>
                    <div style={{fontSize:13}}>{u.display_name || '—'}</div>
                    {u.title && <div style={{fontSize:11.5, color:'var(--text-3)'}}>{u.title}</div>}
                  </td>
                  <td style={{fontSize:12.5, color:'var(--text-2)'}}>{u.email}</td>
                  <td>
                    <select
                      value={primaryRole}
                      onChange={(e)=>changeRole(u, e.target.value)}
                      disabled={u.id === user?.id}
                      style={{background:'var(--bg-1)', border:'1px solid var(--line-2)', borderRadius:6, padding:'5px 8px', fontSize:12.5, color:'var(--text-1)'}}
                    >
                      {ROLE_OPTIONS.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
                    </select>
                  </td>
                  <td style={{textAlign:'right'}}>
                    {u.id !== user?.id && (
                      <button className="btn btn-ghost" style={{padding:'4px 8px', color:'var(--sem-danger)'}} onClick={()=>deleteUser(u)} title="Eliminar">
                        <Icon.Trash size={14}/>
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!loading && users.length === 0 && !error && (
              <tr><td colSpan={4} style={{textAlign:'center', color:'var(--text-3)', fontSize:13, padding:24}}>Aún no hay usuarios.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateUserModal onClose={()=>setShowCreate(false)} onCreated={()=>{ setShowCreate(false); load(); }} />}
    </>
  );
}

export function CreateUserModal({ onClose, onCreated }) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [title, setTitle] = useState('');
  const [role, setRole] = useState('viewer');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const genPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
    let p = '';
    const arr = new Uint32Array(16);
    crypto.getRandomValues(arr);
    for (let i = 0; i < 16; i++) p += chars[arr[i] % chars.length];
    setPassword(p);
  };

  const submit = async () => {
    setError('');
    if (!email || !password) { setError('Correo y contraseña temporal son obligatorios.'); return; }
    if (password.length < 8) { setError('La contraseña debe tener al menos 8 caracteres.'); return; }
    setLoading(true);
    const { data, error: err } = await supabase.functions.invoke('admin-create-user', {
      body: { email, password, display_name: displayName || undefined, title: title || undefined, role },
    });
    setLoading(false);
    if (err || data?.error) {
      setError(typeof data?.error === 'string' ? data.error : 'No se pudo crear el usuario. Revisa los datos.');
      return;
    }
    toast(`Usuario ${email} creado.`);
    onCreated();
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{width:520, padding:32}} onClick={e=>e.stopPropagation()}>
        <h3 className="font-display-heavy" style={{fontSize:26, margin:'0 0 6px 0', letterSpacing:'-0.03em'}}>Crear usuario</h3>
        <p style={{fontSize:13, color:'var(--text-3)', marginBottom:22, lineHeight:1.5}}>
          Se crea una cuenta interna con una contraseña temporal. Compártela de forma segura; en su primer inicio de sesión el usuario deberá crear su propia contraseña.
        </p>

        <div style={{display:'flex', flexDirection:'column', gap:12}}>
          <ModalField label="Correo corporativo" value={email} onChange={setEmail} placeholder="nombre@impacthub.net" type="email"/>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12}}>
            <ModalField label="Nombre" value={displayName} onChange={setDisplayName} placeholder="Nombre y apellido"/>
            <ModalField label="Cargo" value={title} onChange={setTitle} placeholder="p. ej. Consultora"/>
          </div>
          <div style={{display:'flex', flexDirection:'column', gap:6}}>
            <span style={{fontSize:11.5, color:'var(--text-3)', letterSpacing:'0.06em', textTransform:'uppercase'}}>Rol</span>
            <select value={role} onChange={(e)=>setRole(e.target.value)} style={{background:'var(--bg-1)', border:'1px solid var(--line-2)', borderRadius:8, padding:'10px 12px', fontSize:13.5, color:'var(--text-1)'}}>
              {ROLE_OPTIONS.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
            </select>
          </div>
          <div style={{display:'flex', flexDirection:'column', gap:6}}>
            <span style={{fontSize:11.5, color:'var(--text-3)', letterSpacing:'0.06em', textTransform:'uppercase'}}>Contraseña temporal</span>
            <div style={{display:'flex', gap:8}}>
              <input value={password} onChange={(e)=>setPassword(e.target.value)} placeholder="Mínimo 8 caracteres" style={{flex:1, background:'var(--bg-1)', border:'1px solid var(--line-2)', borderRadius:8, padding:'10px 12px', fontSize:13.5, color:'var(--text-1)', fontFamily:'JetBrains Mono'}}/>
              <button className="btn btn-outline" onClick={genPassword} type="button">Generar</button>
            </div>
          </div>
        </div>

        {error && <div style={{fontSize:12.5, color:'var(--sem-danger)', marginTop:14}}>{error}</div>}

        <div style={{display:'flex', gap:8, justifyContent:'flex-end', marginTop:24}}>
          <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={submit} disabled={loading}>{loading ? 'Creando…' : 'Crear cuenta'}</button>
        </div>
      </div>
    </div>
  );
}

function ModalField({ label, value, onChange, placeholder, type = 'text' }) {
  return (
    <label style={{display:'flex', flexDirection:'column', gap:6}}>
      <span style={{fontSize:11.5, color:'var(--text-3)', letterSpacing:'0.06em', textTransform:'uppercase'}}>{label}</span>
      <input type={type} value={value} onChange={(e)=>onChange(e.target.value)} placeholder={placeholder}
        style={{background:'var(--bg-1)', border:'1px solid var(--line-2)', borderRadius:8, padding:'10px 12px', fontSize:13.5, color:'var(--text-1)'}}/>
    </label>
  );
}

// =====================================================
// MFA / TOTP — activar, verificar, desactivar
// =====================================================
export function MfaSettings() {
  const { refreshAal } = useAuth();
  const [factors, setFactors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(null); // { id, qr, secret }
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const loadFactors = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.auth.mfa.listFactors();
    setFactors(data?.totp || []);
    setLoading(false);
  }, []);

  useEffect(() => { loadFactors(); }, [loadFactors]);

  const startEnroll = async () => {
    setError('');
    setBusy(true);
    const { data, error: err } = await supabase.auth.mfa.enroll({ factorType: 'totp', friendlyName: `TOTP ${Date.now()}` });
    setBusy(false);
    if (err) { setError(err.message); return; }
    setEnrolling({ id: data.id, qr: data.totp.qr_code, secret: data.totp.secret });
  };

  const verifyEnroll = async () => {
    if (code.length !== 6) { setError('Introduce el código de 6 dígitos.'); return; }
    setBusy(true);
    setError('');
    const { data: challenge, error: chErr } = await supabase.auth.mfa.challenge({ factorId: enrolling.id });
    if (chErr) { setBusy(false); setError(chErr.message); return; }
    const { error: vErr } = await supabase.auth.mfa.verify({ factorId: enrolling.id, challengeId: challenge.id, code });
    setBusy(false);
    if (vErr) { setError('Código incorrecto. Inténtalo de nuevo.'); return; }
    setEnrolling(null);
    setCode('');
    toast('Autenticación en dos pasos activada.');
    await loadFactors();
    await refreshAal();
  };

  const cancelEnroll = async () => {
    if (enrolling) { try { await supabase.auth.mfa.unenroll({ factorId: enrolling.id }); } catch (e) {} }
    setEnrolling(null);
    setCode('');
    setError('');
  };

  const disable = async (factorId) => {
    if (!(await confirmDialog({ title: 'Desactivar verificación en dos pasos', message: '¿Seguro que quieres desactivar la verificación en dos pasos? Tu cuenta quedará menos protegida.', confirmLabel: 'Desactivar', tone: 'danger', icon: 'Shield' }))) return;
    const { error: err } = await supabase.auth.mfa.unenroll({ factorId });
    if (err) { toast('No se pudo desactivar.', 'AlertTriangle'); return; }
    toast('Verificación en dos pasos desactivada.');
    await loadFactors();
    await refreshAal();
  };

  const verified = factors.filter((f) => f.status === 'verified');

  if (loading) return <div style={{fontSize:13, color:'var(--text-3)'}}>Cargando…</div>;

  if (enrolling) {
    return (
      <div style={{display:'flex', flexDirection:'column', gap:16}}>
        <div style={{display:'flex', gap:20, alignItems:'flex-start'}}>
          <img src={enrolling.qr} alt="Código QR MFA" style={{width:160, height:160, background:'#fff', borderRadius:10, padding:8}}/>
          <div style={{flex:1}}>
            <div style={{fontSize:13.5, color:'var(--text-2)', lineHeight:1.5, marginBottom:10}}>
              Escanea el código con Google Authenticator, 1Password o Authy. Si no puedes escanear, introduce esta clave manualmente:
            </div>
            <code style={{display:'block', fontSize:12.5, fontFamily:'JetBrains Mono', background:'var(--bg-2)', padding:'8px 10px', borderRadius:6, wordBreak:'break-all', color:'var(--text-1)'}}>{enrolling.secret}</code>
          </div>
        </div>
        <div style={{display:'flex', flexDirection:'column', gap:6}}>
          <span style={{fontSize:11.5, color:'var(--text-3)', letterSpacing:'0.06em', textTransform:'uppercase'}}>Código de verificación</span>
          <input inputMode="numeric" maxLength={6} value={code} onChange={(e)=>setCode(e.target.value.replace(/\D/g,'').slice(0,6))} placeholder="000000"
            style={{maxWidth:200, textAlign:'center', letterSpacing:'0.4em', fontSize:20, fontFamily:'JetBrains Mono', padding:'10px', borderRadius:8, border:'1px solid var(--line-2)', background:'var(--bg-1)', color:'var(--text-1)'}}/>
        </div>
        {error && <div style={{fontSize:12.5, color:'var(--sem-danger)'}}>{error}</div>}
        <div style={{display:'flex', gap:8}}>
          <button className="btn btn-primary" onClick={verifyEnroll} disabled={busy}>{busy ? 'Verificando…' : 'Activar MFA'}</button>
          <button className="btn btn-ghost" onClick={cancelEnroll}>Cancelar</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{display:'flex', flexDirection:'column', gap:14}}>
      <div style={{display:'flex', alignItems:'center', gap:12}}>
        <span className={`sem ${verified.length ? 'sem-success' : 'sem-neutral'}`}>
          {verified.length ? 'Activada' : 'Desactivada'}
        </span>
        <span style={{fontSize:12.5, color:'var(--text-3)'}}>
          {verified.length ? 'Tu cuenta pide un código adicional al iniciar sesión.' : 'Añade una capa extra de seguridad con una app de autenticación.'}
        </span>
      </div>

      {verified.map((f) => (
        <div key={f.id} style={{display:'flex', alignItems:'center', gap:12, padding:'12px 14px', background:'var(--bg-2)', borderRadius:8}}>
          <Icon.Shield size={16} style={{color:'var(--sem-success)'}}/>
          <div style={{flex:1, fontSize:13}}>{f.friendly_name || 'App de autenticación (TOTP)'}</div>
          <button className="btn btn-ghost" style={{color:'var(--sem-danger)', fontSize:12.5}} onClick={()=>disable(f.id)}>Desactivar</button>
        </div>
      ))}

      {error && <div style={{fontSize:12.5, color:'var(--sem-danger)'}}>{error}</div>}

      {!verified.length && (
        <div>
          <button className="btn btn-primary" onClick={startEnroll} disabled={busy}>
            <Icon.Shield size={14}/> {busy ? 'Preparando…' : 'Activar verificación en dos pasos'}
          </button>
        </div>
      )}
    </div>
  );
}
